import { clientFacilityStatus } from "../implementation-specialist/misc/client-and-facility-status";
import { emptyPatientChampion, miniChampion, PatientChampion } from "../patient/components/models/patient-champion";
import { PatientEncounter } from "../patient/components/models/patient-encounter";
import {
  emptyPatientCurrentInsurance, emptyPatientWaiver,
  PatientViewModel,
} from '../patient/components/models/patient-view-model';
import { Payment } from "../patient/components/models/payment";
import { StripePaymentMethod } from "../patient/components/models/stripe-payment-method";
import { formatNumberToUSD } from "./calculators";
import { clipboardKeys, formats, StatusColors } from "./enums";
import { ClientStatusCardViewModel } from "./model/client-status-card";
import { Clipboard} from "./model/clipboard";
import { recentlyViewedPatientsObj } from "./model/recentlyViewed";
import {IdProviderFormat, SamlUrl} from "../security/services/SSO";
import _ from "lodash";
import {configSettings} from "./configuration/config-settings";
import { SignatureStatus } from "../patient/components/models/signature-tracking";
import { showErrorStatus } from "../security/state/user-slice";
import { ExternalAccountItem, ImplementationFacility } from "../implementation-specialist/components/implementation-clients/details/models/implementation-facility";
import moment from "moment";
import { EstLevelOfCare } from "../admissions-advisor/models/estimator";
import { AnyAction } from "@reduxjs/toolkit";
import { FormikProps } from "formik";
import { getStateByName, getStateByAbbrev } from './misc/us-states';
import { V2PaymentDetail } from "src/patient/components/models/payment";
import { paynowService } from 'src/guest/services/paynow-service';
import { patientService } from 'src/patient/services/patient-service';
import { userService } from 'src/admin/services/admin-user-service';
import { PAYMENT_METHOD_TYPES } from 'src/patient/components/models/payment-method';
import { PatientPaymentProgram } from 'src/patient/components/models/patient-payment-program';
import { PatientPaymentSchedule } from 'src/patient/components/models/patient-payment.schedule';
import { UserInfoClient } from 'src/admin/models/user-info-client';
import { DetailedLedger } from 'src/shared/model/ledger';
import { 
  getInstanceOfCare, 
  savePatientPaymentProgram,
} from 'src/patient/state/patient-thunk';
import {
  setSelectedEncounter,
  clearSelectedPatient,
  clearEncounter,
  setPatientCurrentInsurance, setPatientWaiver,
} from 'src/patient/state/patient-slice';
import { clearPatientVobList } from 'src/shared/state/vob-slice';
import { ApiBatchLimits } from 'src/shared/enums';
import { showStatus as showStatusUserSlice } from 'src/security/state/user-slice';
import { LedgerUtils } from "./ledger-utils";
import {
  clearEstimate,
  clearSavedEstimates,
} from '../admissions-advisor/state/estimator-slice';
import {clearVOB} from '../admissions-advisor/state/vob-slice';

export class Utils {
    static isObjectEmpty(obj: Object): boolean {
        return Object.keys(obj).length === 0;
    }

    // returns a number formatted into a shorter syntax.
    // example: 5000 becomes 5k, 50100 becomes 50.1k, 5120000 becomes 5.12m.
    // Does not work on numbers < 1000. Input only numbers > 1000.
    static abbreviateNum(number: number, decPlaces = 2) {
        // 2 decimal places => 1.00, 3 => 1.000, etc
        let strNum = "";
        decPlaces = Math.pow(10, decPlaces);
        // Enumerate number abbreviations
        const abbrev = ["k", "m", "b", "t"];
        // Go through the array backwards, so we do the largest first
        for (let i = abbrev.length - 1; i >= 0; i--) {
            // Convert array index to "1000", "1000000", etc
            const size = Math.pow(10, (i + 1) * 3);
            // If the number is bigger or equal do the abbreviation
            if (size <= number) {
                // Here, we multiply by decPlaces, round, and then divide by decPlaces.
                // This gives us nice rounding to a particular decimal place.
                number = Math.round((number * decPlaces) / size) / decPlaces;
                // Handle special case where we round up to the next abbreviation
                if (number === 1000 && i < abbrev.length - 1) {
                    number = 1;
                    i++;
                }
                // Add the letter for the abbreviation
                //  let strNum = number.toString() += abbrev[]
                strNum = "" + number + abbrev[i];
                // We are done... stop
                break;
            }
        }
        return strNum;
    }

    // Array must have a key called 'sortOrder'
    static sortBySortOrder(array: any[]): any[] {
        return array.sort((a, b) => {
            if (a.sortOrder < b.sortOrder) {
                return -1;
            } else if (a.sortOrder > b.sortOrder) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    static getDocumentStatusColor(doesDocumentTypeExist: boolean, documentStatus?: string) {
      if (doesDocumentTypeExist) {
        if (
            documentStatus === SignatureStatus.pending || documentStatus === SignatureStatus.PendingSignature || documentStatus === SignatureStatus.created
            || documentStatus === SignatureStatus.DocumentCreated || documentStatus === SignatureStatus.PendingEsign
            || documentStatus === SignatureStatus.Available || documentStatus === SignatureStatus.SignatureRequested
          ) {
          return StatusColors.warning;
        } else if (documentStatus === SignatureStatus.signed || documentStatus === SignatureStatus.SignatureCompleted) {
          return StatusColors.success;
        }
      } else {
        // document status does not exist. return gray
        return StatusColors.gray;
      }
      return StatusColors.gray;
    }

    static sortAlphabetically(data: any[], keyToSort: string) {
        return data.sort((a, b) => {
            if(a[keyToSort].toLowerCase() < b[keyToSort].toLowerCase()) { return -1; }
            if(a[keyToSort].toLowerCase() > b[keyToSort].toLowerCase()) { return 1; }
            return 0;
        })
    }

    static sortByNumber(data: any[], keyToSort: string) {
        return data.sort((a, b) => {
            if(a[keyToSort] < b[keyToSort]) { return -1; }
            if(a[keyToSort] > b[keyToSort]) { return 1; }
            return 0;
        })
    }

    static convertBoolToYesNo = (value: boolean) => (
        value ? 'Yes' : 'No'
    );

    static convertDate = (date?: Date, showNoDateText?: boolean) => { // converts to 'YYYY-MM-DD format'
      if (!!date && date instanceof Date && !isNaN(date?.getTime())) {
          const convertedDate = new Date(date).toISOString().split("T")[0];
          return convertedDate;
      }
      else {
          return showNoDateText ? 'No Date' : "";
      }
  };

    static convertISODate = (date: Date, details?: boolean) => {
        let year = date.getFullYear();
        let month = date.getMonth() +1;
        let day = date.getDate()
        let hours = date.getHours();
        let minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        let stringMinutes:string = '';
        let strTime = ""

        hours %= 12;
        hours = hours || 12;
        stringMinutes = minutes < 10 ? `0${minutes.toString()}` : minutes.toString();

        if(details) {
            strTime = `${month}/${day}/${year} ${hours}:${stringMinutes} ${ampm}`;
        } else {
            strTime = `${month}/${day}/${year}`;
        }

        return strTime;
    }

    static parseDateTimeString = (dateTimeString: string): Date => {

        const [dateString, timeString] = dateTimeString.split(" ");
        const [month, day, year] = dateString.split("/").map(str => parseInt(str))

        let [hours, minutes, seconds] = timeString.split(":").map(str => parseInt(str))

        const [ampm] = timeString.split(" ")

        if (ampm === "PM" && hours !== 12) {
            hours += 12
        } else if (ampm === "AM" && hours === 12) {
            hours = 0
        }

        const date = new Date(year, month - 1, day, hours, minutes, seconds)

        return date

    }

    /**
     * convert dates that come back from API in ISO format, to a UTC date (NOT local time!), without the time part. Just MM/DD/YYYY.
     */
    static convertISODateAsUTC = (datestring: string) => {
      const dateWithoutTime = datestring?.substring(0, datestring?.indexOf('T'))
      const year = dateWithoutTime?.substring(0, 4);
      const month = dateWithoutTime?.substring(5,7);
      const day = dateWithoutTime?.substring(8, dateWithoutTime?.length);

      const formattedDate = `${month}/${day}/${year}`
      return formattedDate;
    }

    static handleStateParam = (stateParam: any) => {
		let formedState = stateParam;
		const isStateParamNaN = isNaN(formedState);
		if (isStateParamNaN && formedState !== undefined) {
		  if (formedState.length === 2) {
			const stateToSaveCode = getStateByAbbrev(formedState);
			stateToSaveCode !== undefined ? formedState = stateToSaveCode : formedState = {};
		  } else {
			const stateToSave = getStateByName(formedState);
			stateToSave !== undefined ? formedState = stateToSave : formedState = {}
			// TODO: Add condition for handling a misspelled State -- using Smartystreets zipCode API
		  }
		}
		return formedState;
	}

    static isValidDate = (dateObject: any) => {
        const from = this.getMinDate();
        const to = this.getMaxDate();

        if(dateObject === null){
            return false
        }
        if (!moment(dateObject).isBetween(from, to)) {
            return false;
        }
        return new Date(dateObject).toString() !== 'Invalid Date';
    }

    static isFutureDate = (date: Date) => {
        if (moment().diff(moment(date)) > 0) return false;
        return new Date(date).toString() !== 'Invalid Date';
    }

    static getDayOfWeekFromItsNumber = (day: number): string => {
        return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day];
    }

    static trimFormFields = (form: any) => {
        for (let key in form) {
            if (typeof form[key] === "string") {
                const trimmed = form[key].toString().trim()
                form[key] = trimmed;
            }
        }
        return form;
    }

    static deepClone = (data: any) => {
        if (data === undefined || data === null) {
            return data;
        }
        return JSON.parse(JSON.stringify(data)) || [];
    }

    // Formatting a phone number to be ###-###-#### (no spaces, parentheses, etc)
    static formatPhoneNumber = (unformattedNumber: string) => {
        const justNumbers = unformattedNumber.replace(/\D/g, "");
        // add first dash after you type 4th number
        if (justNumbers.length === 4) {
            const formattedNumber = justNumbers.replace(/(\d{3})(\d{1})/g, "$1-$2");
            return formattedNumber;
            // add second dash after you type 7th number
        } else if (justNumbers.length === 7) {
            const formattedNumber = justNumbers.replace(/(\d{3})(\d{3})(\d{1})/g, "$1-$2-$3");
            return formattedNumber;
            // add second dash on 10th number. Can happen if you copy/paste full number into field
        } if (justNumbers.length === 10) {
            const formattedNumber = justNumbers.replace(/(\d{3})(\d{3})(\d{4})/g, "$1-$2-$3");
            return formattedNumber;
        } else {
            return unformattedNumber;
        }
    }

    static getDocumentDisplayStatus = (status?: string) => {
      let resultDocumentStatus = '';
      if (status) {
        if (status === 'sent') {
          resultDocumentStatus = 'Pending Signature'
        } else if (status === 'signed') {
          resultDocumentStatus = 'Signature Completed'
        } else if (status === 'created') {
          resultDocumentStatus = 'Document Created'
        }
      }
      return resultDocumentStatus;
    }

    // Formatting a social security number (SSN) number to be ###-##-####
    static formatSSN = (unformattedNumber: string) => {
        const justNumbers = unformattedNumber.replace(/\D/g, "");
        if (justNumbers.length === 9) {
            const formattedNumber = justNumbers.replace(/(\d{3})(\d{2})(\d{4})/g, "$1-$2-$3");
            return formattedNumber;
        } else {
            return unformattedNumber;
        }
    }

    // Formatting a social security number (SSN) number to be ###-##-####
    static formatTaxIdNumber = (unformattedNumber: string) => {
        const justNumbers = unformattedNumber.replace(/\D/g, "");
        if (justNumbers.length === 3) {
            const formattedNumber = justNumbers.replace(/(\d{2})(\d{1})/g, "$1-$2");
            return formattedNumber;
        } else if (justNumbers.length === 9) {
            const formattedNumber = justNumbers.replace(/(\d{2})(\d{7})/g, "$1-$2");
            return formattedNumber;
        } else {
            return unformattedNumber;
        }
    }

    static formatTaxIdNumberDisplayed = (unformattedNumber: string) => {
        const justNumbers = unformattedNumber.replace(/\D/g, "");
        if (justNumbers.length === 3) {
            const formattedNumber = justNumbers.replace(/(\d{2})(\d{1})/g, "$1-$2");
            return formattedNumber;
        } else if (justNumbers.length === 9) {
            const formattedNumber = "●●-●●●" + justNumbers.slice(5);
            return formattedNumber;
        } else {
            return unformattedNumber;
        }
    }

    static formatTaxIdNumberDisplayedMixin = (unformattedText: string) => {
        const justTargetCharacters = unformattedText.replace(/[^●\d]/, "");
        if (justTargetCharacters.length < 3) {
            return justTargetCharacters
        }
        return `${justTargetCharacters.slice(0, 2)}-${justTargetCharacters.slice(2)}`
    }

    // Strip non-digit characters from the id
    static unformatIdNumber = (formattedNumber: string) => {
        return formattedNumber.replace(/\D/g, "");
    }

    static setPositiveNegativeNumberTextColor = (hasPositiveNegativeEndTextColor: boolean, number: number) => {
        if (hasPositiveNegativeEndTextColor && number < 0) {
            return "negative-number"
        } else if (hasPositiveNegativeEndTextColor && number > 0) {
            return "positive-number"
        }
    }


    static arrayToString(array:Array<any>, keyValue:any, formatter?: formats ){
        let resultString = "";
        const enumKey = Object.keys(clipboardKeys)[Object.values(clipboardKeys).indexOf(keyValue)];
        array && (array?.length > 0) && array.forEach((e,index)=>{
            let value= e[enumKey];
            if(!value) value = ""
            switch (formatter) {
                case formats.usd:
                    value = formatNumberToUSD(Number(value))
                    break;
                case formats.percentage:
                    if(e.coinsuranceType ==="$"){
                        value = formatNumberToUSD(Number(value))
                    } else if (value) {
                        value +="%"
                    }
                    break;
                default:
                    break;
            }
            resultString += `${keyValue} ${array.length > 1 ? index+1 :""}: ${value}\n`
        })
        return resultString
    }

    static objectToString (object:Clipboard, arrayInfo:Array<string>, optionalText?: string | "", ) {
        let resultString = '';
        resultString+= `${optionalText}\n\n`
        for (const [key, value] of Object.entries(object)) {
            const formattedKey= key.replaceAll("_"," ")
            resultString += `${formattedKey}: ${value}\n`
            if(key === "Patient_Financial_Responsibility") {
                // eslint-disable-next-line no-loop-func
                arrayInfo.forEach(e => {
                    resultString += e
                })
                resultString += "Capacity to Pay Report\n"
            }
        }
        return resultString;
    }

    static getPayorName = (props: { patientId?: number | string, payorId?: number, selectedPatient: PatientViewModel, selectedEncounter: PatientEncounter }) => {
        const {patientId, payorId, selectedPatient, selectedEncounter} = props;
        const patientIdNum = Number(patientId);
        if (((patientIdNum === payorId) && (patientIdNum === selectedPatient?.patientId)) || (!payorId)) {
            return `${selectedPatient?.contact?.firstName} ${selectedPatient?.contact?.lastName}`;
        } else {
            const patientChampions = selectedEncounter?.patientChampion;
            let patientName = '';

            if (patientChampions && patientChampions?.length > 0) {
                const champion = patientChampions?.find((c: PatientChampion) =>
                    c.patientChampionId === payorId
                );
                patientName = `${champion?.contact?.firstName} ${champion?.contact?.lastName}`;
            }

            return patientName;
        }
    }

  static getPayorId = (patientId: number, patientChampions: PatientChampion[]) => {
    let payorId;
    if (patientChampions?.length > 0) {
      patientChampions.forEach((champion: PatientChampion) => {
        payorId = champion?.isGuarantor && champion?.patientChampionId;
      })
    } else {
      payorId = patientId;
    }
    return payorId
  }

    static convertPaymentMethodToReadableInfo = (source: StripePaymentMethod) => {
        if (!(source && (source?.object))) {
            return '';
        }
        let result = "";
        if (source?.object === 'card') {
            result = `${source?.name}'s ${source?.brand} card ending in ${source?.last4}`
        } else if (source?.object?.endsWith('bank_account')) {
            result = `${source?.name}'s bank account ending in ${source?.last4}`
        }
        return result;
    }

    static getInfoForTransaction = (transaction: Payment, paymentMethods?: StripePaymentMethod[]) => {
        if (paymentMethods) {
            return paymentMethods.find((paymentMethod: StripePaymentMethod) => paymentMethod?.id === transaction?.paymentMethodUsed?.externalPaymentMethodId)
        }
        else return {} as StripePaymentMethod
    }

    static windowOpenCentered = (url: string) => {
        const width = window.innerWidth;

        const documentWidth = width / 2;
        const documentHeight = 1000;
        const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;


        const systemZoom = width / window.screen.availWidth;
        const left = (width - documentWidth) / 2 / systemZoom + dualScreenLeft;
        window.open(url, "_blank",
            `
      scrollbars=yes,
      width=${documentWidth / systemZoom},
      height=${documentHeight / systemZoom},
      top=20,
      left=${left}
      `
        )
    }

  static checkIfSelectedPayorIsGuarantor(payorId: number, patientAndChampionList: miniChampion[]) {
    const patientOrChampionToCheck = patientAndChampionList.find((champion: miniChampion ) => (
      champion.id === payorId
    ))
    return !!patientOrChampionToCheck?.isGuarantor
  }

  static getPatientsAndChampionsList(selectedPatient: PatientViewModel, selectedEncounter: PatientEncounter, guarantorChampsOnly: boolean) {
    const patientsAndChampionsList = [];
    patientsAndChampionsList.push({
      id: selectedPatient?.patientId,
      contactId: selectedPatient?.contact?.contactId,
      name: `${selectedPatient?.contact?.firstName} ${selectedPatient?.contact?.lastName}`,
      email: selectedPatient?.contact?.email,
      isGuarantor: true, // patient would be the guarantor if they are selected and 'use for recurring payment' is true
      address: `${selectedPatient?.contact?.primaryAddress?.streetAddress1} ${selectedPatient?.contact?.primaryAddress?.streetAddress2} ${selectedPatient?.contact?.primaryAddress?.city}, ${selectedPatient?.contact?.primaryAddress?.state} ${selectedPatient?.contact?.primaryAddress?.zipCode}`
    })

    selectedEncounter?.patientChampion?.length > 0 && (selectedEncounter?.patientChampion[0] !== emptyPatientChampion)
    && selectedEncounter?.patientChampion.forEach((champion: PatientChampion) => {
      if (guarantorChampsOnly) { // we only want to push champions which are Guarantors
        if (champion?.isGuarantor) {
          patientsAndChampionsList.push({
            id: champion?.patientChampionId,
            contactId: champion?.contact?.contactId,
            name: `${champion?.contact?.firstName} ${champion?.contact?.lastName}`,
            email: champion?.contact.email,
            isGuarantor: champion?.isGuarantor,
            address: `${champion?.contact?.primaryAddress?.streetAddress1} ${champion?.contact?.primaryAddress?.streetAddress2} ${champion?.contact?.primaryAddress?.city}, ${champion?.contact?.primaryAddress?.state} ${champion?.contact?.primaryAddress?.zipCode}`
          })
        }
      } else {
        patientsAndChampionsList.push({
          id: champion?.patientChampionId,
          contactId: champion?.contact?.contactId,
          name: `${champion?.contact?.firstName} ${champion?.contact?.lastName}`,
          email: champion?.contact.email,
          isGuarantor: champion?.isGuarantor,
          address: `${champion?.contact?.primaryAddress?.streetAddress1} ${champion?.contact?.primaryAddress?.streetAddress2} ${champion?.contact?.primaryAddress?.city}, ${champion?.contact?.primaryAddress?.state} ${champion?.contact?.primaryAddress?.zipCode}`
        })
      }
    })
    return patientsAndChampionsList;
  }

  static recentlyViewedAddVerificationClients(recentlyViewed: Array<number>, clientId: number){
    if (!recentlyViewed.includes(clientId)){
      recentlyViewed.unshift(clientId)
    } else {
      recentlyViewed.splice(recentlyViewed.indexOf(clientId),1)
      recentlyViewed.unshift(clientId)
    }
    if(recentlyViewed.length>4){
      recentlyViewed.pop()
    }

        return recentlyViewed
    }

    static getExternalPaymentId = (payment: Payment | any) => { // any type to handle for new structure need to update this for new details structure from transaction details in ledger accordion

        let paymentId = ''; // for paid at facility type which doesn't have an external payment ID

        // or conditional to check if it's a bank (ach) payment and not paid at facility
        if (payment?.paymentMethodUsed?.ach || (payment?.paymentTypeId !== 5 && payment?.paymentMethodTypeId === 2)) {
            paymentId = payment?.paymentMethodUsed?.externalPaymentId || payment?.externalPaymentId || ''
        } else if (payment?.paymentMethodUsed?.cardPayment || (payment?.paymentTypeId !== 5 && payment?.paymentMethodTypeId === 1)) { // for card payment with new details structure and not paid at facility
            paymentId = payment?.paymentMethodUsed?.externalPaymentId || payment?.externalPaymentId || ''
        }
        return paymentId;
    }

    static recentlyViewedAddVerificationPatients(recentlyViewedPatients: Array<recentlyViewedPatientsObj>, recentlyViewedPatient: recentlyViewedPatientsObj){
        const isOnRecentlyViewed = recentlyViewedPatients.some(recentlyViewed => {
            if(
                recentlyViewed.patientId === recentlyViewedPatient.patientId &&
                recentlyViewed.encounterId === recentlyViewedPatient.encounterId
            ){
                return true;
            } else {
                return false;
            }
        })

        if (!isOnRecentlyViewed){
            recentlyViewedPatients.unshift(recentlyViewedPatient)
        } else {
            const index = recentlyViewedPatients.findIndex(recentlyViewed => (
                recentlyViewed.patientId === recentlyViewedPatient.patientId &&
                recentlyViewed.encounterId === recentlyViewedPatient.encounterId
            ))
            recentlyViewedPatients.splice(index,1)
            recentlyViewedPatients.unshift(recentlyViewedPatient)
        }
        if(recentlyViewedPatients.length>4){
            recentlyViewedPatients.pop()
        }

        return recentlyViewedPatients
    }

    static getPaymentTypeName(payment: (Payment | V2PaymentDetail) & { [key: string]: any }, hasDispute?: boolean) {
      let paymentType = hasDispute ? 'Unknown' : 'Unknown - Disputed';
      
      const typeId = payment?.paymentType?.paymentTypeId || payment?.paymentTypeId;
      const methodTypeId = payment?.paymentMethodUsed?.paymentMethodTypeId || payment?.paymentMethodTypeId;

      if (payment?.paymentStatus === "Failed" && payment?.externalResponse?.includes('There is no valid recurring payment method')) {
        return "Unknown";
      }
      
      if (typeId === 1 && payment?.paymentAmt === 0) {
        paymentType = "Down Payment"
      } else if (typeId === 5) {
        hasDispute ? paymentType = "At Facility - Disputed" : paymentType = "At Facility"
      } else if (payment?.paymentMethodUsed || methodTypeId) {
        if (hasDispute) {
          paymentType = (methodTypeId === 1) ? `CC - Disputed` : `ACH - Disputed`;
        } else if (methodTypeId === undefined) { // this is for at facility payments
          paymentType = "At Facility"
        } else {
          paymentType = (methodTypeId === 1) ? "CC" : "ACH";
        }
      }
      return paymentType;
    }
    static getLedgerPaymentTypeName(paymentTypeId: number, paymentMethodTypeId: number, paymentAmt: number, hasDispute?: boolean) {
      let paymentType = hasDispute ? 'Unknown' : 'Unknown - Disputed';
      
      if (paymentTypeId === 5 && paymentAmt === 0) {
        paymentType = ""
      } else if (paymentTypeId === 5) {
        hasDispute ? paymentType = "At Facility - Disputed" : paymentType = "At Facility"
      } else if (paymentMethodTypeId) {
        if (hasDispute) {
          paymentType = (paymentMethodTypeId === 1) ? `CC - Disputed` : `ACH - Disputed`;
        } else {
          paymentType = (paymentMethodTypeId === 1) ? "CC" : "ACH";
        }
      }
      return paymentType;
    }

    static getTransactionType(paymentTypeId: number, amt: number) {
        let paymentType = 'Unknown';
        if ((paymentTypeId === 5) && amt === 0) {
            paymentType = ""
        } else if (paymentTypeId === 1) {
            paymentType = "Down Payment"
        } else if (paymentTypeId === 2) {
            paymentType = "Recurring"
        } else if (paymentTypeId === 3) {
            paymentType = "Specialist"
        } else if (paymentTypeId === 4) {
            paymentType = "Self"
        } else if (paymentTypeId === 5 && amt !== 0) {
            paymentType = "Facility"
        }
        return paymentType;
    }

    static getTransactionDetails(payment: Payment | any) {
        let transactionDetails = ""
        if ((payment?.paymentMethodUsed?.cardPayment) || (payment?.paymentMethodUsed?.paymentMethodTypeId === 1)) {
            transactionDetails = `Ending in (${(payment?.paymentMethodUsed?.last4 || 'N/A')})`
        } else if ((payment?.paymentType?.paymentTypeId === 5 || payment?.paymentTypeId === 5) && (payment?.paymentAmt === 0)) {
            transactionDetails = ""
        } else if ((payment?.paymentType?.paymentTypeId === 5) || (payment?.paymentTypeId === 5)) { // conditional handling for new payment details type in ledger accordion
            transactionDetails = 'Paid At Facility'
        } else {
            transactionDetails = `Ending in (${(payment?.paymentMethodUsed?.last4 || 'N/A')})`
        }

        return transactionDetails;
    };

    static getLedgerDetails(paymentMethodTypeId: number, paymentTypeId: number, paymentAmt: number, last4?: string) {
        let transactionDetails = ""
        if (paymentMethodTypeId === 1) {
            transactionDetails = `Ending in (${(last4 || 'N/A')})`
        } else if (paymentTypeId === 5 && paymentAmt === 0) {
            transactionDetails = ""
        } else if (paymentTypeId === 5) {
            transactionDetails = 'Paid At Facility'
        } else {
            transactionDetails = `Ending in (${(last4 || 'N/A')})`
        }

        return transactionDetails;
    };

    static getStatusColor(statusCard: ClientStatusCardViewModel) {
        const foundStatus = clientFacilityStatus.find((status) => {
            return status.status === statusCard.workflowSubStatus.subStatusName;
        })
        if (foundStatus) {
            return foundStatus.color;
        } else {
            return StatusColors.gray;
        }
    }

    static handleNumericValues = (e: Event) => {
        const input = (e.target as HTMLInputElement).value
        const regEx =/^[0-9]*([.][0-9]*)?$/
        return regEx.test(input);
    }

    // Generates a saml redirect link
    // Can take in no parameters or custom full or partial saml options
    static generateSamlUrl = (options : SamlUrl = {}) : string => {
        const defaults ={
            cognitoDomain : `finpass-${configSettings.env}.auth.us-east-1.amazoncognito.com`,
            responseType : 'code',
            IDP :{
                type : "idp_identifier",
                value : "finpay.net"
            },
            clientID : configSettings.aws_userPoolWebClientId,
            redirectURI : `${configSettings.env !== "prod" ? configSettings.home_uri : "https://admin.finpay.net"}`
        }
        const values = _.merge({}, defaults, options);
        return `https://${values.cognitoDomain}/oauth2/authorize?response_type=${values.responseType}&${values.IDP.type}=${values.IDP.value}&client_id=${values.clientID}&redirect_uri=${values.redirectURI}`;

    }

    // Checks to see if entered email domain matches one in the database
    /**
     * Temporary method, only an example of using idp database
     * information to verify email addresses and redirection
     */
    static includesIDP = (email_domain : string, allProviders : IdProviderFormat[]) : boolean  =>{

        for(const provider of allProviders) {
            if (provider.idp_identifier === email_domain) {
                return true;
            }
        }
        return false;

    }
    // Returns error description for error code
    static getErrorByCode = (code : number) : string =>{
        if(code === 404){
            return "Profile not found";
        }
        else{
            return "";
        }
    }

    static isUserRoleEditableRiskClass = (roleName: string) : boolean => {
        const userRolesEditableRiskClass = ['Finpay Super User', 'Super Admin', 'Finpay Admin', 'Administrator'];
        return userRolesEditableRiskClass.includes(roleName);
    }

    static isUserRoleEditableTypeOfAuthDocs = (roleName: string): boolean => {
        const userRolesEditableTypeOfAuthDocs = ['Finpay Super User', 'Super Admin', 'Finpay Admin', 'Administrator']
        return userRolesEditableTypeOfAuthDocs.includes(roleName)
    }

    static isNullStringValue = (value: any): boolean => {
        if ((value === undefined) ||
            (value === null) ||
            (value.trim() === "")) {
            return true;
        }
        return false;
    }

    static robustErrorHandler = (error: { entity: {code: string, message: string}, errorMessage: string, hasErrors: boolean }, thunkAPI: any) => {
        const { entity, errorMessage } = error;
        if (((entity?.code !== "INTERNAL_SERVER_ERROR") && entity?.message) || (entity?.message)) {
            // handle external error
            thunkAPI.dispatch(showErrorStatus(entity?.message))
            throw new Error(entity?.message)
        } else {
            if (entity) {
                const errorMessage = entity.toString();
                thunkAPI.dispatch(showErrorStatus(errorMessage));
                throw new Error(errorMessage);
            } else {
                // handle internal error
                thunkAPI.dispatch(showErrorStatus(errorMessage))
                throw new Error(errorMessage);
            }
        }
    }

    static customErrorHandler = (errorMessage: string, dispatch: (action: AnyAction) => void, actionCreator: (message: string) => AnyAction) => {
        dispatch(actionCreator(errorMessage));
        throw new Error(errorMessage);
      };



    static buildExternalAccountValueWithFacilityName = (externalAccountIds: any[], facilities: ImplementationFacility[][]) : ExternalAccountItem[] => {

        let retAccountIds:ExternalAccountItem[] = [];
        externalAccountIds.forEach((accountId) => {
            // let extAccountGroup: ImplementationFacility[] = [];
            const sameAccountFacilities = facilities.flat().filter(facility => facility.externalAccount.externalAccountId === accountId);
            if (sameAccountFacilities !== undefined) {
                if (sameAccountFacilities.length > 0) {
                    const facilitiesSortedById = Utils.sortByNumber(sameAccountFacilities, 'facilityId');
                    let newName = accountId + " - " + facilitiesSortedById[0].facilityName;
                    retAccountIds.push({ externalId: accountId, externalIdFacilityName: newName});
                } else {
                    retAccountIds.push({ externalId: accountId, externalIdFacilityName: accountId});
                }
            }

        })

        return retAccountIds;
    }

    static getMinDate = () => moment('1900-01-01')
    static getMaxDate = () => moment('2050-12-31')

    static formatDate = (date: string) => {
        if (!moment(date).isBetween(this.getMinDate(), this.getMaxDate())) {
            const currentYear = moment().format('YYYY');
            const m = moment(date,"YYYY-MM-DD").format("MM");
            const d = moment(date,"YYYY-MM-DD").format("DD")
            return moment(`${currentYear}-${m}-${d}`).format('YYYY-MM-DD');
        }
        return date;
    }

    static dateYearValidate = (e: Event) : string => {
        const val = (e.target as HTMLInputElement).value;
        const y = moment(val,"YYYY-MM-DD").format("YYYY");
        const m = moment(val,"YYYY-MM-DD").format("MM");
        const d = moment(val,"YYYY-MM-DD").format("DD")
        return y + '-' + m + '-' + d;
    }

    static isValidDateTextField = (dateObject: any) => {
        const from = this.getMinDate();
        const to = this.getMaxDate();

        if(dateObject === "" || !moment(dateObject).isBetween(from, to)){
            return false
        }
        return true;
    }

    static getLevelOfCareName = (facilityLevelsOfCare: EstLevelOfCare[], levelOfCareId: number) => {
        const LOCitem = facilityLevelsOfCare.find( (item: EstLevelOfCare) => item.facilityLevelOfCareId === levelOfCareId);
        if (LOCitem !== undefined) {
          return LOCitem.facilityLevelOfCareName;
        }
        return "";
    }

    static getLevelOfCareCode = (facilityLevelsOfCare: EstLevelOfCare[], levelOfCareId: number) => {
        const LOCitem = facilityLevelsOfCare.find( (item: EstLevelOfCare) => item.facilityLevelOfCareId === levelOfCareId);
        if (LOCitem !== undefined) {
          return LOCitem.facilityLevelOfCareCode;
        }
        return "";
    }

    static getErrorText = (formik: FormikProps<any>, name: string, touched?: string[]) => {
        touched = touched === undefined ? [name] : touched ;
        const isError = touched.some(fld => formik.touched[fld]) && formik.errors[name];
        return isError ? formik.errors[name] as string : ""
    }

    static isEmptyValue = (value: any) => {
        value = typeof value === "string" ? value.trim() : value;
        const result = [null, undefined, -1, "-1", ""].some(other => value === other)
        return result
    }

    static isPopulatedValue = (value: any) => {
        return !this.isEmptyValue(value);
    }

    static getChannelId(userProfile: any): number {
        let setChannelId: number = 5;
        if (this.checkExternalRole(userProfile)) setChannelId = 2;
        return setChannelId;
    }
      
    static checkExternalRole(userProfile: any): boolean {
        return userProfile.userRole.roleType === 'E';
    }

    static checkFinancialCounselorRole(userProfile: any): boolean {
        return userProfile.userRole.userRoleId === 1 || userProfile.userRole.userRoleId === 9;
    }

    static resetPatientContexts(dispatch: (action: AnyAction) => void){

      dispatch(clearEstimate());              // Estimate: state.admissionsAdvisorContext.estimatorContext.estimate
      dispatch(clearVOB());                   // Vob: state.admissionsAdvisorContext.vobContext.vob
      dispatch(clearSelectedPatient());       // Patient: state.patientContext.selectedPatient
      dispatch(clearEncounter());             // IOC: state.patientContext.selectedEncounter
      dispatch(clearPatientVobList());       // VOB List: state.vobContext.patientVobList
      dispatch(clearSavedEstimates());        // Estimates list: state.admissionsAdvisorContext.estimatorContext.savedEstimates
      dispatch(setPatientCurrentInsurance(emptyPatientCurrentInsurance))    //Patient Current Insurance: state.patientContext.selectedPatientCurrentInsurance
      dispatch(setPatientWaiver(emptyPatientWaiver))                        //Patient Waiver: state.patientContext.selectedPatientWaiver
    }

    static async enroll(params: {
        selectedEncounter: any;
        paymentProgram: any;
        downPayments: DetailedLedger[];
        isCovertDisabled: boolean;
        isZeroDownZeroRecurring: boolean;
        setIsMissingReqModalOpen: (value: boolean, isEnrollFlow?: boolean) => void;
        setIsEnrollLoading: (value: boolean) => void;
        dispatch: any;
        userName: string;
        computedDownPaymentsSum: number | null;
        client?: any;
        isIntegrationEnabled?: boolean;
        isEnrollFlow: boolean | null;
        selectedPatient?: PatientViewModel;
        isZeroDown?: boolean;
        clientCrm?: any;
        processError?: (response: any) => void;
        processUserRecord?: (email: string, isUpdateOnly: boolean, callback: () => void, options: any) => Promise<any>;
        mapToDownPayment?: () => Payment;
    }): Promise<void> {
        const {
        selectedEncounter, 
        paymentProgram, 
        downPayments, 
        isCovertDisabled, 
        isZeroDownZeroRecurring,
        setIsMissingReqModalOpen,
        setIsEnrollLoading,
        dispatch,
        userName,
        computedDownPaymentsSum,
        client,
        isIntegrationEnabled,
        isEnrollFlow,
        selectedPatient,
        isZeroDown,
        clientCrm,
        processError,
        processUserRecord,
        mapToDownPayment
        } = params;

        try {

        // Check if isEnrollFlow is null or undefined
        const isMobileJourney = isEnrollFlow === null || isEnrollFlow === undefined;

        // Special handling for zero down payment
        if (isZeroDown && processUserRecord && mapToDownPayment) {
            // Check for guarantor first, fallback to patient
            let zeroDownEmail = selectedPatient?.contact?.email;
            let zeroDownFirstName = selectedPatient?.contact?.firstName;
            let zeroDownLastName = selectedPatient?.contact?.lastName;

            if (selectedEncounter?.patientChampion?.length) {
                const guarantor = selectedEncounter.patientChampion.find(
                    (champion: PatientChampion) => champion?.isGuarantor && !!champion?.contact?.email
                );
                if (guarantor) {
                    zeroDownEmail = guarantor.contact.email;
                    zeroDownFirstName = guarantor.contact.firstName;
                    zeroDownLastName = guarantor.contact.lastName;
                }
            }

            const userRecord = await processUserRecord(
                zeroDownEmail!,
                false,
                () => {},
                {
                    userEmail: zeroDownEmail,
                    firstName: zeroDownFirstName,
                    lastName: zeroDownLastName,
                    clientId: selectedEncounter.clientId,
                    patientEncounterId: selectedEncounter.patientEncounterId,
                    dispatch
                }
            );
            
            const downPaymentInfo = mapToDownPayment();
            downPaymentInfo.ownerId = userRecord.userId;
            const response = await patientService.createPayment(downPaymentInfo);
            
            if (!response.hasErrors) {
                if (isIntegrationEnabled && clientCrm) {
                    await patientService.integrationUpdate({
                        patientEncounterId: selectedEncounter.patientEncounterId,
                        patientId: selectedEncounter.patientId,
                        crmTypeSlug: clientCrm?.[0]?.crmType?.crmTypeSlug,
                    });
                }
            } else if (processError) {
                processError(response);
                throw new Error("Failed to create zero down payment");
            }
        }

        let totalDownPaymentsSum = computedDownPaymentsSum;
        
        if (totalDownPaymentsSum === null) {
            // Use centralized downpayments sum if available, otherwise fallback to manual calculation
            totalDownPaymentsSum = selectedEncounter?.encounterLedger?.downpaymentsSum || 
                downPayments.reduce((sum: number, ledger: DetailedLedger) => {
                    return sum + (ledger.payment?.paymentAmt || 0);
                }, 0);
        } 

        const pfrAmt = selectedEncounter.adjustedPfrAmt !== null && selectedEncounter.adjustedPfrAmt !== undefined 
        ? selectedEncounter.adjustedPfrAmt 
        : selectedEncounter.pfrAmt;
        
        const isPaidInFull = totalDownPaymentsSum && pfrAmt
        ? totalDownPaymentsSum >= pfrAmt 
        : selectedEncounter.patientPaymentProgram[0].isPaidInFull;

        if (!isMobileJourney) {
            // Check if we need to show the missing requirements modal
            const hasInsufficientDownPayment = isCovertDisabled && 
                ((computedDownPaymentsSum || 0) < (paymentProgram?.downPmtAmt || 0));

                let hasRecurringPaymentMethod;
                
                if(!isPaidInFull) {
                    hasRecurringPaymentMethod = selectedEncounter.patientPaymentMethods?.some((paymentMethod: any) => paymentMethod.metadata.metaData_paymentMethodType === 'RECURRING'); 
                } else {
                    hasRecurringPaymentMethod = true;
                }    

            const doesChampionExist = selectedEncounter?.patientChampion?.length > 0 ||
                selectedEncounter?.hasChampions === false;

            if (hasInsufficientDownPayment || isZeroDownZeroRecurring || !hasRecurringPaymentMethod || !doesChampionExist) {
                setIsMissingReqModalOpen(true, isEnrollFlow);
                return;
            }
            
            setIsEnrollLoading(true);

            if (isEnrollFlow) {
                dispatch(showStatusUserSlice("Enrollment in progress. Please do not navigate away or close the window. The page will refresh automatically when the enrollment is complete."));
            } else {
                dispatch(showStatusUserSlice("Conversion in progress. Please do not navigate away or close the window. The page will refresh automatically when the conversion is complete."));
            }
        }

        const paymentsToCapture = downPayments.filter(detailedLedger => 
            detailedLedger.payment?.isCaptured === false && detailedLedger.payment?.paymentStatus === 'Pending' && 
            (detailedLedger.payment?.paymentMethodTypeId === PAYMENT_METHOD_TYPES.CARD.paymentMethodTypeId ||
            detailedLedger.payment?.paymentMethodTypeId === PAYMENT_METHOD_TYPES.ACH.paymentMethodTypeId) 
        );
        if (downPayments.length > 0) {
            await paynowService.updatePaymentProgram(paymentProgram.patientPaymentProgramId!, {
                isAchVerified: true,
                isAch: isEnrollFlow ? undefined : false
            });
        }
        await Promise.all(
            paymentsToCapture.map(async (downPayment) => {
            const result = await paynowService.updatePaymentDetails(-2, { 
                isCaptured: true,
                paymentId: downPayment.payment?.paymentId
            });
            if (result.hasErrors) {
                throw new Error(`Failed to capture payment: ${result.entity}`);
            }
            })
        );
    
    
    
                // Making PUT to update the paymentSchedule startDt
    const scheduleStartDt = paymentProgram.patientPaymentSchedule.nextPaymentDueDt;


       if (!isMobileJourney && !isPaidInFull) {
            if (!scheduleStartDt) {
                dispatch(showErrorStatus("Schedule start date is missing."));
                throw new Error("Schedule start date is null - recurring payment setup may have failed");
            }
            await paynowService.setScheduleStartDt(
                paymentProgram.patientPaymentSchedule.patientPaymentScheduleId!,
                scheduleStartDt
            );
        }
    
        let documentResponse;
        if (!isMobileJourney) { // no docs sent for full payment in mobile journey
            if (isPaidInFull) { 
                documentResponse = await patientService.spaDocument(selectedEncounter.patientEncounterId);
            } else if (!isEnrollFlow) { 
                documentResponse = await patientService.legacyInstallmentDocument(paymentProgram.patientPaymentProgramId);
            } else {
                documentResponse = await patientService.installmentDocument(selectedEncounter.patientEncounterId);
            }
            
            if (documentResponse.hasErrors) {
                throw new Error(documentResponse.entity.message ?? documentResponse.errorMessage);
            }
            
            // Only dispatch the message if it's not empty
            if (documentResponse.entity.message && documentResponse.entity.message !== "") {
                dispatch(showStatusUserSlice(documentResponse.entity.message));
            } //legacyInstallmentDocument is not returning a message
        } 
        if (isPaidInFull) { //Full payment = Converted, Paid None for Enroll Flow and Converted, New, None for Convert Flow
            const statusResult = await patientService.saveStatus({
                workFlow: {
            workflowId: 3,
            workflowStatus: {
                workflowStatusId: 16, //Paid 
                workflowStatusDesc: '',
            },
            workflowSubStatus: {
                workflowSubStatusId: 32, //None
                workflowSubStatusDesc: '',
            },
            },
            encounterId: selectedEncounter.patientEncounterId!,
                patientId: selectedEncounter.patientId!,
            });
    
            if (statusResult.hasErrors) {
                throw new Error(`Failed to update full payment workflow status: ${statusResult.entity}`);
            }

        } else { 
            const statusResult = await patientService.saveStatus({
            workFlow: {
            workflowId: 3,
            workflowStatus: {
                workflowStatusId: isMobileJourney ? 12 : 6, //if enroll or mobile then Converted, if Convert then Followup Needed
                workflowStatusDesc: '',
            },
            workflowSubStatus: {
                workflowSubStatusId: isMobileJourney ? 23 : 30, //if enroll or mobile then None, if Convert then Converted Pending Authorization
                workflowSubStatusDesc: '',
                },
            },
            encounterId: selectedEncounter.patientEncounterId!,
                patientId: selectedEncounter.patientId!,
            });
    
            if (statusResult.hasErrors) {
                throw new Error(`Failed to update workflow status: ${statusResult.entity}`);
            }
        }
    
        const finClearanceStatusDt = moment().toISOString();
        const convertDt = moment().toISOString();
    
        let iocResult;
        
        if (isPaidInFull) { //Paid, None for Enroll Flow and Converted, Paid None for Convert Flow
            iocResult = await patientService.saveIOCWithPartialObject(
                {
                finClearanceStatus: 'Paid, None',
                finClearanceStatusDt,
                convertDt,
                convertUserId: userName,
                isConverted: true
                },
                selectedEncounter.patientEncounterId!
            );
        } else {
            iocResult = await patientService.saveIOCWithPartialObject(
                {
                finClearanceStatus: isMobileJourney ? 'Converted' : 'Converted, Pending - Awaiting Authorizations',
                finClearanceStatusDt,
                convertDt,
                convertUserId: userName,
                isConverted: true
                },
                selectedEncounter.patientEncounterId!
            );
        }
    
        if (iocResult && iocResult.hasErrors) {
            throw new Error(`Failed to update financial clearance status: ${iocResult.entity}`);
        }
        
        if (isMobileJourney) {
            await paynowService.updatePaymentProgram(
                paymentProgram.patientPaymentProgramId,
                {
                  patientPaymentSchedule: {
                    pfrAmt: selectedEncounter.pfrAmt,
                    scheduleStatus: 'Active'
                  },
                  patientId: selectedEncounter.patientId,
                  patientEncounterId: selectedEncounter.patientEncounterId
                }
              );
         } else {
            const saveResult = await dispatch(savePatientPaymentProgram({
                paymentProgram: {
                patientPaymentProgramId: selectedEncounter.patientPaymentProgram?.[0]?.patientPaymentProgramId || 0,
                patientPaymentSchedule: {
                    pfrAmt: selectedEncounter.pfrAmt || 0,
                    scheduleStatus: 'Active',
                } as PatientPaymentSchedule
                } as PatientPaymentProgram,
                patientId: selectedEncounter.patientId,
                encounterId: selectedEncounter.patientEncounterId,
                showNotify: false,
            }));
    
            if (saveResult.meta.requestStatus === 'rejected') {
                throw new Error("Failed to update payment program status");
            }

            if (selectedEncounter && dispatch) {
                await LedgerUtils.fetchLedger({
                    encounterId: selectedEncounter.patientEncounterId,
                    dispatch,
                });
            }
         }
    
        let linkedUserId = selectedPatient?.contact?.email;
        let firstName = selectedPatient?.contact?.firstName;
        let lastName = selectedPatient?.contact?.lastName;
        let middleName = selectedPatient?.contact?.middleName;
        
        if (selectedEncounter?.patientChampion?.length) {
            const guarantor = (selectedEncounter?.patientChampion.filter((champion: PatientChampion) => (
            champion?.isGuarantor && !!champion?.contact?.email
            )))[0];
            if (guarantor) {
            linkedUserId = guarantor.contact.email;
            firstName = guarantor.contact.firstName;
            lastName = guarantor.contact.lastName;
            middleName = guarantor.contact.middleName; 
            }
        } 
        
        const user = await userService.getAllUsers(0, ApiBatchLimits.users, { userName: linkedUserId || '' });

        if (user.entity && user.entity.length > 0) {
            const isVerified = await userService.getUserById(user.entity[0].userId);
            await patientService.saveIOCWithPartialObject(
            { linkedUserId, accountHolderUserId: user.entity[0].userId },
            selectedEncounter.patientEncounterId,
            );
            if (isVerified.entity.cognitoStatus.UserStatus === 'CONFIRMED') {
                await patientService.sendNotification({
                    username: linkedUserId || '',  
                    templateId: 5,
                });
            } else {
                await userService.resendInvitation(user.entity[0].userId);
            }
        } else {
            const userClientScopeToSave = new UserInfoClient();
            userClientScopeToSave.clientId = selectedEncounter?.clientId;
            userClientScopeToSave.isFacilityLevel = true;
            userClientScopeToSave.allowedFacilities = [];
    
            const userToCreate = {
            userName: linkedUserId,
            firstName,
            lastName,
            userScope: "FINPAY",
            clientId: selectedEncounter?.clientId,
            entityId: 0,
            isActive: true,
            isPatient: true,
            convRunNum: 1,
            userRole: {
                userRoleId: 4,
                roleName: "Account Holder",
                roleDesc: "User Role for Patients or Patient Guarators",
                roleType: "E",
                isActive: true
            },
            allowedClients: [userClientScopeToSave],
            patientEncounterId: selectedEncounter.patientEncounterId
            };
            const newUser = await userService.createOrUpdateUser(userToCreate);
            const isVerifiedNewUser = await userService.getUserById(newUser.entity.userId);
            
            await patientService.saveIOCWithPartialObject(
            { linkedUserId: newUser.entity.userName, accountHolderUserId: newUser.entity.userId },
            selectedEncounter.patientEncounterId
            );

            if (isVerifiedNewUser.entity.cognitoStatus.UserStatus === 'CONFIRMED') {
                await patientService.sendNotification({
                    username: linkedUserId || '',  
                    templateId: 5,
                });
            } else {
                await userService.resendInvitation(newUser.entity.userId); //resend invitation to the newly created user
            }
        }
        
        const iocResponse = await dispatch(getInstanceOfCare({
            patientId: selectedEncounter.patientId,
            encounterId: selectedEncounter.patientEncounterId,
        }));
    
        if (iocResponse?.type !== "patientContext/getInstanceOfCare/rejected") {
            if (iocResponse.payload && (iocResponse.payload as { selectedPatientEncounter: any }).selectedPatientEncounter) {
            const selectedPatientEncounter = (iocResponse.payload as { selectedPatientEncounter: any }).selectedPatientEncounter;
            dispatch(setSelectedEncounter(selectedPatientEncounter));
            }
        }
        } catch (error) {
        // If any error occurs, mark ACH as not verified
        await paynowService.updatePaymentProgram(paymentProgram.patientPaymentProgramId!, {
            isAchVerified: false
        });
        dispatch(showErrorStatus((error as Error).message));
        throw error;
        } finally {
        setIsEnrollLoading(false);
        }
        
        if (isIntegrationEnabled && client) {
        await patientService.integrationUpdate({
            patientEncounterId: selectedEncounter.patientEncounterId,
            patientId: selectedEncounter.patientId,
            crmTypeSlug: client?.clientCrm?.[0]?.crmType?.crmTypeSlug,
        });
        }
    }
}
